// Layer 4 (UI) — the route's header row and band (locked comp
// `.impeccable/mocks/decision/documents.html` B): the title with this shift's
// round sheets and the next publish as header facts, then Paul's four
// training KPIs as one band, each figure over its comparison and its top rule
// in the state of that figure. The band stays on every tab.
// Renders `Scene.transform.ts` and `TrainingCompliance.transform.ts`; derives nothing.

import { Fragment, type ReactNode } from 'react';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import { ActorChip } from '../advisory/PipelineStrip';
import type { DocumentsHeaderFact } from './Scene.transform';
import type { ComparisonSegment, TrainingBandCell } from './TrainingCompliance.transform';

function Segments({ segments }: { segments: ReadonlyArray<ComparisonSegment & { tone?: 'warn' }> }) {
  return (
    <>
      {segments.map((segment, index) => {
        const strong = Boolean(segment.strong);
        const nowrap = Boolean(segment.nowrap);
        if (!strong && !nowrap) return <Fragment key={index}>{segment.text}</Fragment>;
        return (
          <span
            key={index}
            className={cn(
              strong && 'font-semibold',
              strong && (segment.tone === 'warn' ? 'text-[var(--state-warning)]' : 'text-ink-secondary'),
              nowrap && 'whitespace-nowrap',
            )}
          >
            {segment.text}
          </span>
        );
      })}
    </>
  );
}

/** The page's one header row (comp `.title-row`): the title left, the header facts right. */
export function DocumentsHeader({
  title,
  facts,
  actions,
}: {
  title: string;
  facts: readonly DocumentsHeaderFact[];
  actions?: ReactNode;
}) {
  return (
    <header className="flex shrink-0 flex-wrap items-end gap-x-6 gap-y-3">
      <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'mr-auto min-w-0')}>{title}</h1>
      <div className="flex items-center gap-3">
        <dl className="flex flex-wrap items-stretch tabular-nums" aria-label="This shift">
          {facts.map((fact, index) => (
            <div
              key={fact.id}
              data-header-fact={fact.id}
              className={cn('flex min-w-0 flex-col gap-px px-4', index === 0 ? 'pl-0' : 'border-l border-[var(--hairline-strong)]', index === facts.length - 1 ? 'pr-0' : '')}
            >
              <dt className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">{fact.label}</dt>
              <dd className="whitespace-nowrap text-lg font-semibold leading-tight text-ink-primary">
                {fact.value}
                {fact.unit ? <>{' '}<span className="text-xs font-semibold text-muted">{fact.unit}</span></> : null}
              </dd>
              <dd className="flex items-center gap-1.5 whitespace-nowrap text-[11.5px] text-muted">
                <span>
                  <Segments segments={fact.comparison} />
                </span>
                {fact.actor ? <ActorChip actor="Human">{fact.actor}</ActorChip> : null}
              </dd>
            </div>
          ))}
        </dl>
        {actions}
      </div>
    </header>
  );
}

/** Paul's four KPIs as one band (comp `.band`, unordered cells, no notches). */
export function DocumentsBand({ cells }: { cells: readonly TrainingBandCell[] }) {
  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label="Training compliance"
      data-documents-band
      className="shrink-0 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-2 xl:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-band-cell={cell.id}
            data-band-tone={cell.tone}
            className="relative flex min-w-0 flex-col gap-[3px] border-b border-r border-[var(--hairline)] px-4 pb-[11px] pt-2.5 last:border-r-0 xl:border-b-0 [&:nth-child(2)]:border-r-0 xl:[&:nth-child(2)]:border-r"
          >
            {cell.tone !== 'none' ? (
              <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: vizToneColor(cell.tone) }} />
            ) : null}
            <span className="text-[11px] font-semibold uppercase leading-tight tracking-[0.07em] text-muted">{cell.label}</span>
            <span className="text-2xl font-semibold leading-[1.15] tracking-[-0.01em] tabular-nums text-ink-primary" data-band-figure={cell.id}>
              {cell.figure}
              {cell.unit ? <>{' '}<span className="text-sm font-semibold tracking-normal text-muted">{cell.unit}</span></> : null}
            </span>
            <span className="text-[11.5px] leading-[1.35] tabular-nums text-muted" data-band-comparison={cell.id}>
              <Segments segments={cell.comparison} />
            </span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

